import {XIcon} from '@primer/octicons-react'
import {Button, Heading, Text} from '@primer/react-brand/esm'
import {useCallback, useRef, useState, type MouseEventHandler, type RefObject} from 'react'

import type {MobileOS} from '@github-ui/get-os'
import {sendEvent} from '@github-ui/hydro-analytics'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {EmailCaptureForm} from '../../../../../../brand/components/EmailCaptureForm/EmailCaptureForm'

import Styles from './MobileEmailHandoff.module.css'

export const EMAIL_HANDOFF_DIALOG_ID = 'github-app-mobile-email-handoff-dialog'

export type EmailHandoffLocation = 'hero' | 'highlight' | 'cta_banner'
export type EmailHandoffSubmit = (email: string, location: EmailHandoffLocation) => void | Promise<void>

export type EmailHandoffCopy = {
  closeLabel: string
  dialogBody: string
  dialogHeading: string
  errorMessage: string
  formLabels: Record<EmailHandoffLocation, string>
  inputLabel: string
  inputPlaceholder: string
  pendingLabel: string
  requiredMessage: string
  submitLabel: string
  successHeading: string
  successMessage: string
  triggerLabel: string
}

type GitHubAppEmailHandoffFormProps = {
  copy: EmailHandoffCopy
  location: EmailHandoffLocation
  platform: MobileOS
  submitEmail: EmailHandoffSubmit
}

type FocusReturnRef = {
  current: HTMLButtonElement | null
}

type EmailHandoffDialogProps = {
  copy: EmailHandoffCopy
  dialogRef: RefObject<HTMLDialogElement | null>
  location: EmailHandoffLocation
  platform: MobileOS
  returnFocusRef: FocusReturnRef
  submitEmail: EmailHandoffSubmit
}

export function createEmailHandoffSubmitter(endpoint: string): EmailHandoffSubmit {
  return async function submitEmail(email, location) {
    const response = await reactFetchJSON(endpoint, {
      body: {email, location},
      method: 'POST',
    })

    if (!response.ok) {
      throw new Error(`Email handoff request failed with status ${response.status}`)
    }
  }
}

function trackEmailHandoff(location: EmailHandoffLocation, platform: MobileOS) {
  const analyticsEvent = getAnalyticsEvent({
    action: 'Email download link',
    tag: 'button',
    context: `mobile_handoff_${platform.toLowerCase()}`,
    location,
  })['data-analytics-event']

  sendEvent('analytics.click', JSON.parse(analyticsEvent) as Record<string, string>)
}

export function GitHubAppEmailHandoffForm({copy, location, platform, submitEmail}: GitHubAppEmailHandoffFormProps) {
  return (
    <EmailCaptureForm
      className={Styles.EmailHandoffForm}
      errorMessage={copy.errorMessage}
      formLabel={copy.formLabels[location]}
      inputName={`github_app_email_${location}`}
      label={copy.inputLabel}
      labelVariant="floating"
      onPending={() => trackEmailHandoff(location, platform)}
      onSubmit={email => submitEmail(email, location)}
      pendingMessage={copy.pendingLabel}
      placeholder={copy.inputPlaceholder}
      reserveMessageSpace={false}
      requiredMessage={copy.requiredMessage}
      submitLabel={copy.submitLabel}
      successMessage={`${copy.successHeading}. ${copy.successMessage}`}
    />
  )
}

export function useEmailHandoffDialog(): {
  dialogRef: RefObject<HTMLDialogElement | null>
  location: EmailHandoffLocation
  openDialog: (location: EmailHandoffLocation) => MouseEventHandler<HTMLButtonElement>
  returnFocusRef: FocusReturnRef
} {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const returnFocusRef = useRef<HTMLButtonElement>(null)
  const [location, setLocation] = useState<EmailHandoffLocation>('hero')

  const openDialog = useCallback(
    (nextLocation: EmailHandoffLocation): MouseEventHandler<HTMLButtonElement> =>
      event => {
        const dialog = dialogRef.current
        if (!dialog) return

        setLocation(nextLocation)
        returnFocusRef.current = event.currentTarget
        dialog.showModal()

        requestAnimationFrame(() => {
          dialog.querySelector<HTMLElement>('input[type="email"], [tabindex="-1"]')?.focus()
        })
      },
    [],
  )

  return {dialogRef, location, openDialog, returnFocusRef}
}

export function EmailHandoffDialog({
  copy,
  dialogRef,
  location,
  platform,
  returnFocusRef,
  submitEmail,
}: EmailHandoffDialogProps) {
  function closeDialog() {
    dialogRef.current?.close()
  }

  function returnFocus() {
    const trigger = returnFocusRef.current
    returnFocusRef.current = null
    trigger?.focus()
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={`${EMAIL_HANDOFF_DIALOG_ID}-heading`}
      className={Styles.EmailHandoffDialog}
      id={EMAIL_HANDOFF_DIALOG_ID}
      onClose={returnFocus}
    >
      <div className={Styles.EmailHandoffDialogHeader}>
        <Heading as="h2" id={`${EMAIL_HANDOFF_DIALOG_ID}-heading`} size="5">
          {copy.dialogHeading}
        </Heading>

        <Button
          aria-label={copy.closeLabel}
          className={Styles.EmailHandoffDialogClose}
          type="button"
          variant="subtle"
          onClick={closeDialog}
        >
          <XIcon size={16} />
        </Button>
      </div>

      <Text as="p" className={Styles.EmailHandoffDialogBody} size="200" variant="muted">
        {copy.dialogBody}
      </Text>

      <GitHubAppEmailHandoffForm copy={copy} location={location} platform={platform} submitEmail={submitEmail} />
    </dialog>
  )
}
